import { useMutation, useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Page from "components/base/Page";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CANCEL_TEAMS_SUBSCRIPTION, FETCH_CHANNEL_SNAPSHOTS } from "./graphql";
import { formatDateTime, PAGE_SIZE } from "./utils";

const groupThreads = (messages) => {
  const repliesByParent = new Map();
  const rootMessages = [];

  messages.forEach((message) => {
    if (message.replyToId) {
      const replies = repliesByParent.get(message.replyToId) || [];
      replies.push(message);
      repliesByParent.set(message.replyToId, replies);
    } else {
      rootMessages.push(message);
    }
  });

  const rootIds = new Set(rootMessages.map((message) => message.messageId));
  const orphanedReplies = messages.filter(
    (message) => message.replyToId && !rootIds.has(message.replyToId),
  );

  return [
    ...rootMessages.map((message) => ({
      message,
      replies: repliesByParent.get(message.messageId) || [],
    })),
    ...orphanedReplies.map((message) => ({ message, replies: [] })),
  ];
};

const Message = ({ message, isReply = false }) => {
  return (
    <div
      className={`py-3 ${
        isReply ? "ml-5 border-l-2 border-sky-100 dark:border-sky-900 pl-4" : ""
      }`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="font-semibold">
          {message.fromUserDisplayName || "Unknown user"}
        </span>
        <span className="opacity-60">
          {formatDateTime(message.createdDatetime)}
        </span>
        {message.isDeleted ? (
          <span className="rounded bg-red-100 px-1.5 py-0.5 text-red-700 dark:bg-red-900 dark:text-red-200">
            Deleted
          </span>
        ) : null}
      </div>
      <div className="mt-2 whitespace-pre-wrap text-sm">
        {message.text || <span className="opacity-50">No text content</span>}
      </div>
    </div>
  );
};

const ChannelDetailPage = ({ teamId, channelId, subscription }) => {
  const navigate = useNavigate();
  const [offset, setOffset] = useState(0);
  const [cancelSubscription, cancelResult] = useMutation(
    CANCEL_TEAMS_SUBSCRIPTION,
  );
  const { data, loading, error } = useQuery(FETCH_CHANNEL_SNAPSHOTS, {
    variables: {
      teamId,
      channelId,
      options: { limit: PAGE_SIZE, offset },
    },
    fetchPolicy: "network-only",
  });
  const snapshots = data?.teamsChannelSnapshots?.results || [];
  const total = data?.teamsChannelSnapshots?.total || 0;
  const threads = useMemo(() => groupThreads(snapshots), [snapshots]);
  const hasPreviousPage = offset > 0;
  const canLoadNext = offset + snapshots.length < total;
  const from = snapshots.length ? offset + 1 : 0;
  const to = offset + snapshots.length;
  const title = `${subscription?.teamName || teamId} / ${
    subscription?.channelName || channelId
  }`;

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const handleDeleteSubscription = async () => {
    if (
      !window.confirm(
        `Cancel the subscription for channel ${
          subscription?.channelName || channelId
        }?`,
      )
    ) {
      return;
    }

    try {
      const { data } = await cancelSubscription({
        variables: { subscriptionId: subscription.subscriptionId },
      });
      const result = data?.cancelTeamsSubscription;
      if (
        !result?.ok &&
        !result?.localUpdated &&
        result?.graphStatusCode !== 404
      ) {
        Alert("error", result?.message || "Unable to cancel subscription.");
        return;
      }
      Alert("success", result.message || "Subscription cancelled.");
      navigate("/teams");
    } catch (error) {
      Alert("error", error.message);
    }
  };

  return (
    <Page title={title} backTo="/teams">
      <div className="grid grid-cols-12 gap-6 p-6">
        <div className="col-span-8 flex flex-col space-y-6">
          <div className="card p-0">
            <div className="p-5 flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-700">
              <div>
                <h5>Messages</h5>
                <div className="mt-1 text-xs opacity-60">
                  {total} snapshot message{total === 1 ? "" : "s"} total
                </div>
              </div>
            </div>
            {!threads.length ? (
              <div className="p-6 opacity-60">
                No messages found. The channel may not have been synced yet.
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {threads.map(({ message, replies }) => (
                  <div key={message.messageId} className="px-5">
                    <Message message={message} />
                    {replies.map((reply) => (
                      <Message key={reply.messageId} message={reply} isReply />
                    ))}
                  </div>
                ))}
              </div>
            )}
            <div className="p-3 flex justify-end items-center space-x-2 opacity-70 border-t border-gray-100 dark:border-gray-700">
              {hasPreviousPage ? (
                <Button prev onClick={() => setOffset(offset - PAGE_SIZE)} />
              ) : null}
              <div>
                {from}-{to}
              </div>
              {canLoadNext ? (
                <Button next onClick={() => setOffset(offset + PAGE_SIZE)} />
              ) : null}
            </div>
          </div>
        </div>
        <div className="col-span-4 flex flex-col space-y-6">
          <div className="card space-y-3">
            <div>
              <div className="text-xs opacity-60">Subscription</div>
              <div className="mt-1 break-all">
                {subscription?.subscriptionId || "-"}
              </div>
            </div>
            <div>
              <div className="text-xs opacity-60">Status</div>
              <div className="mt-1">{subscription?.status || "-"}</div>
            </div>
            <div>
              <div className="text-xs opacity-60">Team ID</div>
              <div className="mt-1 break-all">{teamId}</div>
            </div>
            <div>
              <div className="text-xs opacity-60">Channel ID</div>
              <div className="mt-1 break-all">{channelId}</div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              danger
              loading={cancelResult.loading}
              disabled={!subscription?.subscriptionId}
              onClick={handleDeleteSubscription}
            >
              Delete Subscription
            </Button>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default ChannelDetailPage;
